import { ReactNode, useCallback, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { useBoolean, useInterval } from 'ahooks'
import { useAuth0 } from 'lib/auth'
import { useAppDispatch } from 'lib/redux'
import {
  clearAccessToken,
  setAccessToken,
  setOrganization,
} from 'lib/redux/auth'
import { routes } from 'config'
import { Button, Result } from 'components/common'

interface Props {
  children?: ReactNode
}

const SessionCheck = ({ children }: Props): JSX.Element => {
  const { user, isLoading, isAuthenticated, getAccessTokenSilently, logout } =
    useAuth0()
  const dispatch = useAppDispatch()
  const [gettingAccessToken, gettingAccessTokenAction] = useBoolean(true)
  const [error, setError] = useState(null)

  // アクセストークン取得関数
  const getAccessToken = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently({
        audience: process.env.GATSBY_AUTH0_AUDIENCE,
      })
      dispatch(setAccessToken(accessToken))
    } catch (e) {
      console.error(e)
      setError(e)
      dispatch(clearAccessToken())
    } finally {
      gettingAccessTokenAction.setFalse()
    }
  }, [dispatch, getAccessTokenSilently, gettingAccessTokenAction])

  useEffect(() => {
    if (typeof user === 'undefined') return

    dispatch(
      setOrganization({ name: user.orgName, displayName: user.orgDisplayName })
    )
  }, [dispatch, user])

  useEffect(() => {
    if (isLoading) {
      // Do not anything
    } else if (isAuthenticated) {
      getAccessToken()
    } else {
      // 認証されていなければログインへリダイレクト
      navigate(routes.login)
    }
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    dispatch,
    getAccessToken,
  ])

  // 定期的にアクセストークンを取得
  useInterval(() => {
    getAccessToken()
  }, 30000)

  if (isLoading || gettingAccessToken) return <>Loading...</>

  if (error) {
    return (
      <>
        <Result
          status="warning"
          title="認証処理中に何らかの問題が発生しました。再度ログインしてください。"
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() =>
                logout({
                  returnTo: window.location.origin,
                })
              }
            >
              ログアウト
            </Button>
          }
        />
      </>
    )
  }

  return <>{children}</>
}

export default SessionCheck
